import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';

import { SystemStatus } from './SystemStatus';
import { Workers } from './Workers';
import { CaptureAppReleases } from './CaptureAppReleases';
import { PhysicalDevices } from './PhysicalDevices';
import { DataIntegrity } from './DataIntegrity';

/**
 * Operations — the platform-ops surfaces (System status, Workers, Capture-app
 * releases) consolidated from three separate pages into one tabbed page.
 * Each panel is the original page component in `embedded` mode (its own
 * h4/description suppressed; page-level actions like Refresh / Upload APK
 * kept). The active tab lives in the URL (`?tab=status|workers|releases`) so
 * the old routes deep-link straight to the right tab.
 */
const TABS = [
  {
    key: 'status',
    label: 'System status',
    render: (slot: HTMLElement | null) => (
      <SystemStatus embedded actionsSlot={slot} />
    ),
  },
  { key: 'workers', label: 'Workers', render: () => <Workers embedded /> },
  {
    key: 'releases',
    label: 'Capture-app releases',
    render: (slot: HTMLElement | null) => (
      <CaptureAppReleases embedded actionsSlot={slot} />
    ),
  },
  {
    key: 'devices',
    label: 'Devices',
    render: () => <PhysicalDevices embedded />,
  },
  {
    key: 'integrity',
    label: 'Data integrity',
    render: () => <DataIntegrity embedded />,
  },
] as const;

export function Operations() {
  const [params, setParams] = useSearchParams();
  // Host for the active tab's page-level actions (System status Refresh,
  // Capture-app Upload APK), portaled onto the tab line.
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  const requested = params.get('tab');
  const active = Math.max(
    0,
    TABS.findIndex((t) => t.key === requested),
  );

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h4">Operations</Typography>
        <Typography variant="body2" color="text.secondary">
          Platform health, worker scaling, capture-app releases and the
          cross-tenant device fleet.
        </Typography>
      </Stack>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Tabs
          value={active}
          onChange={(_e, v: number) =>
            setParams({ tab: TABS[v].key }, { replace: true })
          }
          aria-label="Operations sections"
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box
          ref={setActionsSlot}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}
        />
      </Box>

      <Box>{TABS[active].render(actionsSlot)}</Box>
    </Stack>
  );
}
